import { Typography } from 'antd';
import { CSSProperties, useCallback, useEffect, useMemo } from 'react';
import { Customer } from 'store/customers/customer';
import useCustomers from 'hooks/useCustomers';
import useDebounce from 'utils/useDebounce';
import FormSelect, { SelectOption } from '../formSelect/FormSelect';
import { Cell, EmailCell, OptionRows, TopRow, TruncatedText } from './styles';
import EllipsisTextTooltip from 'components/EllipsisTextTooltip';

const normalize = (v?: string | null) => (v ?? '').toString().trim();
const getName = (customer: Customer) => normalize(customer.name);
const getEmail = (customer: Customer) => normalize(customer.contact.email);
const getEori = (customer: Customer) => normalize(customer.eori);

type Props = {
    customerId: string | undefined;
    placeholder?: string;
    disabled?: boolean;
    fieldMeta?: import('formik').FieldMetaProps<any>;
    fieldHelper?: import('formik').FieldHelperProps<any>;
    style?: CSSProperties;
    excludeIds?: string[];
    onChange: (id?: string) => void;
    onClear: () => void;
};

const CustomerSelect = ({
    customerId,
    placeholder = 'Select Customer',
    onChange,
    onClear,
    disabled,
    fieldMeta,
    fieldHelper,
    style,
    excludeIds,
}: Props) => {
    const { entities, getCustomer, customers, listCustomers } = useCustomers();

    useEffect(() => {
        if (customerId && !entities[customerId]) {
            getCustomer(customerId);
        }
    }, [customerId, entities, getCustomer]);

    const fetchCustomers = useCallback(
        async (query?: string) => {
            await listCustomers(query ? { query, size: 200, active: 'ACTIVE' } : { size: 200 });
        },
        [listCustomers]
    );

    const debouncedSearchCustomers = useDebounce((q: string) => {
        fetchCustomers(q?.trim() || undefined);
    }, 500);

    const options: SelectOption[] = useMemo(() => {
        const listed = (customers.list ?? [])
            .filter((ind) => !excludeIds?.includes(ind.id))
            .map((ind) => ({ id: ind.id, value: getName(ind) || getEmail(ind) }));

        /**
         * Every search replaces the list, and a stored customer is not in it at all until one is
         * run, so the selected one is added when it is missing. Without an option carrying its id
         * the select has nothing to take a label from and falls back to showing the raw id.
         */
        const selected = customerId ? entities[customerId] : undefined;

        if (selected && !listed.some(({ id }) => id === selected.id)) {
            listed.unshift({ id: selected.id, value: getName(selected) || getEmail(selected) });
        }

        return listed;
    }, [customers.list, excludeIds, customerId, entities]);

    return (
        <FormSelect
            hideKeys
            disabled={disabled}
            fieldMeta={fieldMeta}
            fieldHelper={fieldHelper}
            filterOption={false} // Use only BE filtering
            placeholder={placeholder}
            value={customerId}
            optionLabelProp="label"
            selectOptions={options}
            onSearch={debouncedSearchCustomers}
            style={style}
            onChange={(id) => {
                onChange?.(id);
                listCustomers({ size: 200 });
            }}
            onClear={() => {
                listCustomers({ size: 200 });
                onClear?.();
            }}
            dropdownStyle={{ minWidth: 'max-content' }}
            renderOptionContent={(item) => {
                const customer = entities[item.id];
                if (!customer) return item.value;

                return (
                    <OptionRows>
                        <TopRow>
                            <Cell>
                                <Typography>{getEori(customer)}</Typography>
                            </Cell>
                            <EllipsisTextTooltip tooltipTitle={getName(customer)}>
                                <TruncatedText>{getName(customer)}</TruncatedText>
                            </EllipsisTextTooltip>
                        </TopRow>
                        <EmailCell>
                            <Typography>{getEmail(customer)}</Typography>
                        </EmailCell>
                    </OptionRows>
                );
            }}
        />
    );
};

export default CustomerSelect;
